import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getIndexName } from '../constants';
import { createBrowserSearchClient } from '../utils/search/create-browser-search-client';
import type { BrowserSearchClient } from '../utils/search/create-browser-search-client';
import {
	generateUrlFromIndexUiState,
	parseUrlToIndexUiState,
	pathnameIsJobsListingPath,
	type GenerateUrlFromIndexUiStateOptions,
} from '../utils/algolia/url-handling';
import { refinementKeyToAlgoliaNumericAttribute } from '../utils/algolia/refinement-key-to-algolia-numeric-attribute';
import {
	readPrimaryLocationFilterValues,
	reorderSearchHitsForPrimaryLocationBoost,
} from '../utils/location/reorder-search-hits-for-primary-location-boost';
import { usePageContext } from 'vike-react/usePageContext';
import { extractFacetsFromSearchResults, FACET_ATTRIBUTES, type FacetsData } from '../utils/facets/facets-shared';
import { trackSearchQuery, trackFilterAdd, trackFilterRemove, trackSortChange, clearQueryID, trackEvent } from '../utils/analytics/analytics';
import {
	buildSearchFiltersJson,
	resolveSearchSortLabel,
	trackSearchAnalyticsGa4Debounced,
} from '../utils/analytics/track-search-analytics-ga4';
import { isSrpNonFilterQueryParam } from '../utils/search/srp-non-filter-query-params';
import { useAppSelector, useAppDispatch, useRehydrated } from '../redux/hooks';
import { useSelector, shallowEqual } from 'react-redux';
import type { RootState } from '../redux/createStore';
import { isToolsPage } from '../utils/routing/route-utils';
import { addSearch } from '../redux/features/savedSearchesSlice';
import {
	normalizeSavedSearchUrlInput,
	shouldSkipPersistingSavedSearchUrl,
} from '../utils/saved-searches/normalize-saved-search-url';
import { setFinlandFilterEnabled } from '../redux/features/finlandFilterSlice';
import { shouldIgnoreEmptyInitialFiltersFromLayout } from '../utils/search/should-ignore-empty-initial-filters-from-layout';
import { isExpectedEnvironmentError } from '../utils/shared/is-expected-environment-error';

type AlgoliaFilters = string | Record<string, unknown> | undefined;

const IS_SEARCH_CONTEXT_DEBUG_ENABLED = import.meta.env.DEV;

function debugLog(...args: unknown[]): void {
	if (!IS_SEARCH_CONTEXT_DEBUG_ENABLED) return;
	console.warn(...args);
}

function urlSyncOptionsForPathname(pathname: string): GenerateUrlFromIndexUiStateOptions {
	return { forceJobsListingForCompany: pathnameIsJobsListingPath(pathname) };
}

// Helper to create stable string representation of filters for comparison
function stableStringify(value: unknown): string {
	if (value === null || typeof value !== 'object') return JSON.stringify(value);
	if (Array.isArray(value)) return JSON.stringify(value.map(stableStringify));
	const obj = value as Record<string, unknown>;
	const keys = Object.keys(obj).sort();
	const normalized: Record<string, unknown> = {};
	for (const k of keys) normalized[k] = obj[k];
	return JSON.stringify(normalized);
}

function filtersKey(f: AlgoliaFilters): string {
	if (typeof f === 'string') return `s:${f}`;
	if (!f) return 'u:';
	return `o:${stableStringify(f)}`;
}

interface SearchProviderProps {
	children: React.ReactNode;
	indexName: string;
	initialQuery?: string;
	initialFilters?: AlgoliaFilters;
	initialPage?: number;
	initialResults?: any; // Raw Algolia results JSON (server-fetched)
	syncToUrl?: boolean; // if true, push URL on state changes
	initialSort?: 'relevance' | 'date' | 'salary';
	/** When outside Vike tree (e.g. SRP island), pass locale so SearchProvider does not rely on usePageContext(). */
	localeOverride?: string;
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface SearchContextValue {
	// Removed indexName - not used by any consumer (they use getIndexName directly)
	query: string;
	setQuery: (q: string) => void;
	filters: AlgoliaFilters;
	setFilters: (f: AlgoliaFilters) => void;
	page: number;
	setPage: (p: number) => void;
	results: any | null;
	/** SSR/initial results; used so facets are not overwritten until user triggers a new search */
	initialResults: any | null;
	/** True while Algolia is fetching and there is no usable result set yet for the current view. */
	isSearchLoading: boolean;
	refetch: () => Promise<void>;
  sort: 'relevance' | 'date' | 'salary';
  setSort: (s: 'relevance' | 'date' | 'salary') => void;
  // Helper to generate URL for a specific page
  getPageUrl: (targetPage: number) => string;
  // Map view support - removed mapBounds (not read by consumers, only setMapBounds is used)
  setMapBounds: (bounds: MapBounds | null) => void;
  viewMode: 'list' | 'map';
  setViewMode: (mode: 'list' | 'map') => void;
}

export const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export function SearchProvider({ children, indexName, initialQuery = '', initialFilters, initialPage = 0, initialResults, syncToUrl = true, initialSort = 'relevance', initialViewMode = 'list', localeOverride }: SearchProviderProps & { initialViewMode?: 'list' | 'map' }) {
	// Get locale from page context (or override when outside Vike tree, e.g. SRP island)
	const pageContext = usePageContext();
	const locale = (pageContext?.locale ?? localeOverride ?? 'en') as string;
	const dispatch = useAppDispatch();
	const isRehydrated = useRehydrated();

	// Get Finland filter state from Redux (with fallback for SSR)
	// Use useSelector with a custom equality function to prevent SearchProvider from re-rendering
	// when Redux updates but the value hasn't actually changed. This is critical because Redux
	// store updates were causing SearchProvider to re-render, which then caused all consumers
	// (SearchResultsPage, CustomHits) to re-render unnecessarily.
	// The equality function returns true if values are equal, preventing re-renders
	const isFinlandEnabled = useSelector(
		(state: RootState) => state.finlandFilter?.enabled ?? true,
		(a, b) => a === b // Strict equality - useSelector already does this by default, but explicit is clearer
	);
	
  
	
	// Use a key to force re-initialization when initialResults change from null to actual data
	const providerKey = React.useMemo(() => {
		return `${indexName}-${initialQuery}-${JSON.stringify(initialFilters)}-${initialPage}-${!!initialResults}-${initialSort}`;
	}, [indexName, initialQuery, initialFilters, initialPage, initialResults, initialSort]);

	const searchClientRef = useRef<BrowserSearchClient | null>(null);
	const ensureSearchClient = useCallback(() => {
		if (typeof window === 'undefined') return null;
		if (searchClientRef.current) return searchClientRef.current;
		searchClientRef.current = createBrowserSearchClient();
		return searchClientRef.current;
	}, []);
	// Normalize provided index to the base (locale) index without any sort suffix
	const baseIndexName = useMemo(() => (indexName ? indexName.replace(/_(postedDate_desc|salaryRangeMin_desc)$/,'') : 'en_search') , [indexName]);

	const [query, setQuery] = useState<string>(initialQuery);
	const filtersRef = useRef<AlgoliaFilters>(initialFilters);
	const [page, setPage] = useState<number>(initialPage);
	const [filtersVersion, setFiltersVersion] = useState<number>(0);
	const filtersKeyRef = useRef<string>(filtersKey(initialFilters));
	const [results, setResults] = useState<any | null>(initialResults ?? null);
	// Use a ref to track current results for comparison in callbacks (avoids stale closures)
	const resultsRef = useRef<any | null>(initialResults ?? null);
	
	// Track what's calling setResults to identify the source of changes
	const setResultsCallStackRef = useRef<string[]>([]);
	// Track what's calling setFilters to identify the source of changes
	const setFiltersCallStackRef = useRef<string[]>([]);
	
	// Track hit IDs as a string to detect when actual content changes (not just reference)
	const hitIdsPrevRef = useRef<string>('');
	const hitIdsStableRef = useRef<string>('');
	const hitIds = useMemo(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] hitIds useMemo RUNNING');
		}
		const hits = results?.hits || [];
		const newHitIds = hits.map((hit: any) => hit?.objectID || hit?.id || '').join(',');
		const prevHitIds = hitIdsPrevRef.current;
		
		// If the value hasn't changed, return the previous stable reference
		if (prevHitIds === newHitIds && prevHitIds !== '') {
			if (typeof window !== 'undefined') {
				debugLog('🟢 [SearchContext] hitIds useMemo - value unchanged, returning previous reference', {
					hitIds: newHitIds,
					hitsArrayRefChanged: results?.hits !== (resultsRef.current?.hits),
				});
			}
			return hitIdsStableRef.current;
		}
		
		if (prevHitIds !== newHitIds && typeof window !== 'undefined' && prevHitIds !== '') {
			debugLog('🔵 [SearchContext] hitIds useMemo recalculated - IDs changed', {
				prevHitIds,
				newHitIds,
				hitsArrayRefChanged: results?.hits !== (resultsRef.current?.hits),
			});
		}
		hitIdsPrevRef.current = newHitIds;
		hitIdsStableRef.current = newHitIds;
		return newHitIds;
	}, [results?.hits]);
	
	// Track nbHits and page separately to catch other content changes
	const resultsContentKeyPrevRef = useRef<string>('');
	const resultsContentKeyStableRef = useRef<string>('');
	const resultsContentKey = useMemo(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] resultsContentKey useMemo RUNNING');
		}
		const newKey = `${results?.nbHits || 0}-${results?.page || 0}-${hitIds}`;
		const prevKey = resultsContentKeyPrevRef.current;
		
		// If the key value hasn't changed, return the previous stable reference
		if (prevKey === newKey && prevKey !== '') {
			if (typeof window !== 'undefined') {
				debugLog('🟢 [SearchContext] resultsContentKey useMemo - value unchanged, returning previous reference', {
					key: newKey,
				});
			}
			return resultsContentKeyStableRef.current;
		}
		
		if (prevKey !== newKey && typeof window !== 'undefined' && prevKey !== '') {
			debugLog('🔵 [SearchContext] resultsContentKey useMemo recalculated - key changed', {
				prevKey,
				newKey,
				prevNbHits: resultsRef.current?.nbHits,
				currNbHits: results?.nbHits,
				prevPage: resultsRef.current?.page,
				currPage: results?.page,
				prevHitIds: hitIdsPrevRef.current,
				currHitIds: hitIds,
			});
		}
		resultsContentKeyPrevRef.current = newKey;
		resultsContentKeyStableRef.current = newKey;
		return newKey;
	}, [results?.nbHits, results?.page, hitIds]);
	
	// Keep ref in sync with state and track changes
	// Only run when resultsContentKey changes (actual content change, not just reference)
	const prevContentKeyRef = useRef<string>('');
	useEffect(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] useEffect (resultsRef sync) RUNNING', {
				resultsContentKey,
				prevContentKey: prevContentKeyRef.current,
			});
		}
		
		// Skip if content key hasn't actually changed (defensive check)
		// This handles the case where useMemo recalculates but produces the same value
		if (prevContentKeyRef.current === resultsContentKey && prevContentKeyRef.current !== '') {
			if (typeof window !== 'undefined') {
				debugLog('⚠️ [SearchContext] useEffect ran but content key unchanged - skipping update');
			}
			return;
		}
		
		const prevResults = resultsRef.current;
		const isFirstRender = prevContentKeyRef.current === '';
		
		// If effect runs, content changed (because resultsContentKey is in dependency array)
		// Only log if we have previous results AND it's not the first render
		if (!isFirstRender && prevResults !== null && typeof window !== 'undefined') {
			const prevHitIds = (prevResults?.hits || []).map((hit: any) => hit?.objectID || hit?.id || '').join(',');
			const prevContentKey = `${prevResults?.nbHits || 0}-${prevResults?.page || 0}-${prevHitIds}`;
			const prevHitsRef = prevResults?.hits;
			const currHitsRef = results?.hits;
			const hitsArrayRefChanged = prevHitsRef !== currHitsRef;
			
			// Only log if the content actually changed (not just the key reference)
			if (prevContentKey !== resultsContentKey) {
				debugLog('🔴 [SearchContext] results CONTENT changed', {
					prevContentKey,
					currContentKey: resultsContentKey,
					prevNbHits: prevResults?.nbHits,
					currNbHits: results?.nbHits,
					prevPage: prevResults?.page,
					currPage: results?.page,
					prevHitIds,
					currHitIds: hitIds,
					hitsArrayRefChanged,
					callStack: setResultsCallStackRef.current.slice(-3), // Last 3 call sites
				});
			} else if (typeof window !== 'undefined') {
				debugLog('🟢 [SearchContext] results CONTENT unchanged (key changed but content same)', {
					contentKey: resultsContentKey,
				});
			}
		} else if (isFirstRender && typeof window !== 'undefined') {
			debugLog('🟢 [SearchContext] First render - initializing resultsRef');
		}
		
		// Always update the refs to keep them in sync
		resultsRef.current = results;
		prevContentKeyRef.current = resultsContentKey;
	}, [resultsContentKey]);
	
	// Wrap setResults to capture call stack and track what's changing
	const setResultsWithTracking = useCallback((newResults: any) => {
		const prevResults = resultsRef.current;
		
		if (typeof window !== 'undefined') {
			// Capture call stack to see where setResults is being called from
			if (typeof Error !== 'undefined') {
				const stack = new Error().stack;
				const stackLines = stack?.split('\n').slice(2, 8) || []; // Skip Error and setResultsWithTracking
				setResultsCallStackRef.current = stackLines.map(line => line.trim());
			}
			
			// Only log if this is not the initial set (prevResults is not null)
			if (prevResults !== newResults && prevResults !== null) {
				const prevHitsRef = prevResults?.hits;
				const currHitsRef = newResults?.hits;
				const hitsArrayRefChanged = prevHitsRef !== currHitsRef;
				const contentChanged = prevResults?.nbHits !== newResults?.nbHits || prevHitsRef?.length !== currHitsRef?.length;
				
				if (hitsArrayRefChanged || contentChanged) {
					debugLog('🔴 [SearchContext] setResults called', {
						prevNbHits: prevResults?.nbHits,
						currNbHits: newResults?.nbHits,
						hitsArrayRefChanged,
						contentChanged,
						callStack: setResultsCallStackRef.current.slice(-3), // Last 3 call sites
					});
				}
			}
		}
		
		setResults(newResults);
	}, []);
	// Separate results for map mode (geo search)
	// Initialize with SSR geo results if available and in map mode
	const [mapResults, setMapResults] = useState<any | null>(() => {
		// If we have initialResults and we're in map mode, use them as initial map results
		if (initialViewMode === 'map' && initialResults) {
			if (typeof window !== 'undefined') {
				debugLog('[SearchContext] Initializing mapResults from SSR geo results', { nbHits: initialResults.nbHits });
			}
			return initialResults;
		}
		return null;
	});
	
	// Helper to check if two results objects are effectively the same
	const areResultsEqual = useCallback((a: any, b: any): boolean => {
		if (a === b) return true;
		if (!a || !b) return a === b;
		
		// Compare key fields that matter for rendering
		if (a.nbHits !== b.nbHits) return false;
		if (a.page !== b.page) return false;
		if (a.nbPages !== b.nbPages) return false;
		
		// Compare hits array length and objectIDs (most efficient check)
		const hitsA = Array.isArray(a.hits) ? a.hits : [];
		const hitsB = Array.isArray(b.hits) ? b.hits : [];
		if (hitsA.length !== hitsB.length) return false;
		
		// Compare objectIDs to detect if hits actually changed
		for (let i = 0; i < hitsA.length; i++) {
			const idA = hitsA[i]?.objectID || hitsA[i]?.id;
			const idB = hitsB[i]?.objectID || hitsB[i]?.id;
			if (idA !== idB) return false;
		}
		
		return true;
	}, []);
	
	// Stable results reference - only update if results actually changed
	const stableResultsRef = useRef<any | null>(initialResults ?? null);
	// Track a version number that only increments when results actually change (not just reference)
	// This is used in the context value memo to prevent recalculation when only reference changes
	const [resultsVersion, setResultsVersion] = useState<number>(0);
	const resultsVersionRef = useRef<number>(0);
	// Track previous results reference for version tracking
	const prevResultsRefForVersion = useRef<any | null>(initialResults ?? null);
	
	// Compute effective results (no side effects in useMemo)
	const effectiveResults = useMemo(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] effectiveResults useMemo RUNNING');
		}
		const currentResults = results || initialResults;
		const previousResults = stableResultsRef.current;
		
		// Only update the stable reference if results actually changed
		if (areResultsEqual(previousResults, currentResults)) {
			return previousResults;
		}
		
		// Results actually changed - update stable reference (but don't set state here)
		stableResultsRef.current = currentResults;
		if (typeof window !== 'undefined') {
			debugLog('[SearchContext] effectiveResults changed', {
				prevNbHits: previousResults?.nbHits,
				currNbHits: currentResults?.nbHits,
			});
		}
		return currentResults;
	}, [results, initialResults, areResultsEqual]);
	
	// Update resultsVersion in an effect (not during render)
	// Track previous results reference to detect when it changes
	useEffect(() => {
		const currentResults = results || initialResults;
		const previousResults = stableResultsRef.current;
		const prevResultsRef = prevResultsRefForVersion.current;
		
		// Update version if:
		// 1. Results reference changed (new results object, even if content is same)
		// 2. Results content actually changed (different hit IDs, nbHits, etc.)
		// This ensures that when filters change and new results come in, we always update
		// even if the content appears the same (which shouldn't happen, but we want to be safe)
		const resultsRefChanged = prevResultsRef !== currentResults;
		const resultsContentChanged = !areResultsEqual(previousResults, currentResults);
		
		if (resultsRefChanged || resultsContentChanged) {
			resultsVersionRef.current += 1;
			setResultsVersion(resultsVersionRef.current);
			prevResultsRefForVersion.current = currentResults;
			if (typeof window !== 'undefined') {
				debugLog('🪝 [SearchContext] useEffect (resultsVersion update) RUNNING - incrementing version', {
					version: resultsVersionRef.current,
					prevNbHits: previousResults?.nbHits,
					currNbHits: currentResults?.nbHits,
					resultsRefChanged,
					resultsContentChanged,
				});
			}
		}
	}, [results, initialResults, areResultsEqual]);
    const [sort, setSort] = useState<'relevance' | 'date' | 'salary'>(initialSort);
	const [isLoading, setIsLoading] = useState<boolean>(!initialResults);
	
	// Store callbacks in refs to prevent context value recreation when they change
	// Initialize with placeholder functions that will be replaced after callbacks are defined
	const performSearchRef = useRef<() => Promise<void>>(async () => {});
	const performMapSearchRef = useRef<() => Promise<void>>(async () => {});
	const setFiltersCallbackRef = useRef<(f: AlgoliaFilters) => void>(() => {});
	const setSortWithTrackingRef = useRef<(s: 'relevance' | 'date' | 'salary') => void>(() => {});
	const getPageUrlRef = useRef<(targetPage: number) => string>(() => '#');
	
	const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
	// Normalize initialViewMode: 'grid' from server/cookies is treated as 'list' (responsive grid)
	const normalizedInitialViewMode: 'list' | 'map' = (initialViewMode === 'map' ? 'map' : 'list');
	// Load viewMode from localStorage on client, fallback to initialViewMode
	// Use function form to only read localStorage on client-side
	const [viewMode, setViewModeState] = useState<'list' | 'map'>(() => {
		if (typeof window === 'undefined') return normalizedInitialViewMode;
		// Check both 'layout' and 'jobcrawls.viewMode' for backward compatibility; 'grid' → 'list'
		const stored = localStorage.getItem('layout') || localStorage.getItem('jobcrawls.viewMode');
		if (stored === 'map') return 'map';
		if (stored === 'list' || stored === 'grid') return 'list';
		return normalizedInitialViewMode;
	});
	
	// Wrapper to save to localStorage when viewMode changes
	const setViewMode = useCallback((mode: 'list' | 'map') => {
		setViewModeState(mode);
		if (typeof window !== 'undefined') {
			try {
				// Save to both keys for backward compatibility
				localStorage.setItem('layout', mode);
				localStorage.setItem('jobcrawls.viewMode', mode);
			} catch (err) {
				debugLog('[SearchContext] Failed to save viewMode to localStorage:', err);
			}
		}
	}, []);
	
	// Automatically switch from 'map' to 'list' when on tools pages
	// This ensures map view is not available on tools pages
	// Note: Tools pages use their own local viewMode state, but this is a defensive measure
	// for any code that might use SearchContext's viewMode on tools pages
	useEffect(() => {
		// Skip on non-search pages
		if (!syncToUrl) return;
		
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] useEffect (viewMode tools page check) RUNNING');
		}
		if (typeof window === 'undefined') return;
		
		// Get current pathname from pageContext or window.location
		const currentPathname = pageContext?.urlPathname || pageContext?.urlParsed?.pathname || window.location.pathname;
		if (isToolsPage(currentPathname) && viewMode === 'map') {
			debugLog('[SearchContext] Switching from map to list view on tools page:', currentPathname);
			setViewMode('list');
		}
	}, [viewMode, setViewMode, pageContext, syncToUrl]);
	
	const didHydrateRef = useRef<boolean>(false);
	/**
	 * True after we've skipped the first client Algolia search because `initialResults` already
	 * contained SSR/prefetch hits. Must NOT set `didHydrateRef` — that ref is reserved for the URL-sync
	 * effect's first-run guard. If we flip `didHydrateRef` here, URL sync skips its safe early return,
	 * runs full `replaceState` with stale `query` from props (often "" on CSR), and wipes the real URL
	 * before `handleUrl` runs.
	 */
	const ssrInitialSearchHandledRef = useRef<boolean>(false);
	const suppressUrlSyncRef = useRef<boolean>(false);
    const prevQueryRef = useRef<string>(initialQuery || '');
    const prevFiltersRef = useRef<string>(JSON.stringify(initialFilters || {}));
    const prevSortRef = useRef<'relevance' | 'date' | 'salary'>(initialSort);
	const initialPropsRef = useRef({ indexName, initialQuery, initialFilters, initialPage, hasInitialResults: Boolean(initialResults) });
	const isSearchingRef = useRef<boolean>(false);
	// Store the SSR pathname to ensure hydration consistency
	const ssrPathnameRef = useRef<string | null>(null);

	// Simple in-memory cache for session (normal search)
	const cacheRef = useRef<Map<string, any>>(new Map());
	// Separate cache for map mode geo searches
	const mapCacheRef = useRef<Map<string, any>>(new Map());

	const cacheKey = useMemo(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] cacheKey useMemo RUNNING');
		}
		return JSON.stringify({ indexName: baseIndexName, query, filters: filtersRef.current, page, sort, filtersVersion });
	}, [baseIndexName, query, page, sort, filtersVersion]);

	// Generate deterministic SRP ID from query + filters
	const generateSrpId = useCallback((query: string, filters: AlgoliaFilters): string => {
		const filtersStr = typeof filters === 'string' ? filters : JSON.stringify(filters || {});
		const combined = `${query || ''}|${filtersStr}`;
		// Simple hash function for deterministic ID
		let hash = 0;
		for (let i = 0; i < combined.length; i++) {
			const char = combined.charCodeAt(i);
			hash = ((hash << 5) - hash) + char;
			hash = hash & hash; // Convert to 32bit integer
		}
		return `srp_${Math.abs(hash).toString(36)}`;
	}, []);

	// Format filters for human-readable display
	const formatFiltersApplied = useCallback((filters: AlgoliaFilters): string => {
		if (!filters) return '';
		if (typeof filters === 'string') {
			// Try to parse if it's JSON
			try {
				const parsed = JSON.parse(filters);
				return Object.entries(parsed)
					.map(([key, value]) => {
						if (Array.isArray(value)) {
							return `${key}: ${value.join(', ')}`;
						}
						return `${key}: ${value}`;
					})
					.join('; ');
			} catch {
				return filters;
			}
		}
		return Object.entries(filters)
			.map(([key, value]) => {
				if (Array.isArray(value)) {
					return `${key}: ${value.join(', ')}`;
				}
				return `${key}: ${value}`;
			})
			.join('; ');
	}, []);

	// Track SRP view with debouncing
	const srpViewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastSrpIdRef = useRef<string | null>(null);

	// Separate geo search function for map mode
	const performMapSearch = useCallback(async () => {
		debugLog('🔵 [Algolia] performMapSearch ENTRY', { viewMode, hasMapBounds: !!mapBounds });
		// Only perform map search if in map mode and bounds exist
		if (viewMode !== 'map' || !mapBounds) {
			debugLog('🟡 [Algolia] performMapSearch SKIPPED (not map mode or no bounds)', { viewMode, hasMapBounds: !!mapBounds });
			return;
		}

		// Prevent multiple simultaneous searches
		if (isSearchingRef.current) {
			return;
		}

		// Create a stable cache key for map search
		const currentCacheKey = JSON.stringify({ 
			indexName: baseIndexName, 
			query, 
			filters: filtersRef.current, 
			mapBounds,
			isFinlandEnabled,
			type: 'map'
		});
		
		// Serve from map cache if found
		if (mapCacheRef.current.has(currentCacheKey)) {
			const cachedResult = mapCacheRef.current.get(currentCacheKey);
			debugLog('🟡 [Algolia] CACHE HIT: map search (skipping API call)', {
				indexName: baseIndexName,
				query: query || '',
				cacheKey: currentCacheKey,
				nbHits: cachedResult?.nbHits || 0,
			});
			// Only update state if results actually changed
			if (!areResultsEqual(mapResults, cachedResult)) {
				setMapResults(cachedResult);
			}
			return;
		}

		isSearchingRef.current = true;
		setIsLoading(true);

		const client = ensureSearchClient();
		if (!client) {
			isSearchingRef.current = false;
			setIsLoading(false);
			return;
		}

		try {
			// Geo search params - only use insideBoundingBox for geo search
			const params: Record<string, any> = {
				query: query || '',
				page: 0, // Map search always starts at page 0
				hitsPerPage: 1000, // Get more results for map view
				clickAnalytics: true,
				// Algolia insideBoundingBox format: [lat1, lng1, lat2, lng2]
				// We have: { north, south, east, west }
				// Convert to: [south, west, north, east] (southwest corner, then northeast corner)
				insideBoundingBox: [[mapBounds.south, mapBounds.west, mapBounds.north, mapBounds.east]],
			};

			// Apply filters if any
			if (typeof filtersRef.current === 'string') {
				params.filters = filtersRef.current;
			} else if (filtersRef.current && typeof filtersRef.current === 'object' && filtersRef.current !== null && Object.keys(filtersRef.current).length > 0) {
				const facetFilters: (string | string[])[] = [];
				const numericFilters: string[] = [];
				const numericRule: Record<string, 'gte' | 'lte'> = {
					salaryRangeMin: 'gte',
					vacationWeeks: 'gte',
					companyHeadcountRangeMax: 'lte',
					lastSeenMin: 'gte',
				};
				
				Object.entries(filtersRef.current as Record<string, unknown>).forEach(([attribute, raw]) => {
					if (isSrpNonFilterQueryParam(attribute)) {
						return;
					}
					let values = Array.isArray(raw) ? raw as Array<string | number> : [raw as string | number];
					const isExclude = attribute.startsWith('exclude_');
					const actualAttribute = isExclude ? attribute.replace('exclude_', '') : attribute;
					
					if (numericRule[actualAttribute] && !isExclude) {
						values.forEach((v) => {
							const num = typeof v === 'number' ? v : Number(String(v).trim());
							const isValid = actualAttribute === 'lastSeenMin' 
								? (!Number.isNaN(num) && isFinite(num))
								: (!Number.isNaN(num) && isFinite(num) && num > 0);
							if (isValid) {
								const algoliaAttribute = refinementKeyToAlgoliaNumericAttribute(actualAttribute);
								const filterString = numericRule[actualAttribute] === 'gte' 
									? `${algoliaAttribute}>=${num}` 
									: `${algoliaAttribute}<=${num}`;
								numericFilters.push(filterString);
							}
						});
					} else if (!numericRule[actualAttribute]) {
						if (isExclude) {
							values.forEach((v) => {
								facetFilters.push(`NOT ${actualAttribute}:${String(v)}`);
							});
						} else {
							if (values.length === 1) {
								facetFilters.push(`${attribute}:${String(values[0])}`);
							} else if (values.length > 1) {
								facetFilters.push(values.map((v) => `${attribute}:${String(v)}`));
							}
						}
					}
				});
				
				// Add isFinland facet filter if enabled
				if (isFinlandEnabled) {
					facetFilters.push('isFinland:true');
				}
				
				if (facetFilters.length) params.facetFilters = facetFilters;
				if (numericFilters.length) params.numericFilters = numericFilters;
			} else {
				// Even if no other filters, apply isFinland if enabled
				if (isFinlandEnabled) {
					params.facetFilters = ['isFinland:true'];
				}
			}

			debugLog('[SearchContext] Map geo search params:', JSON.stringify(params, null, 2));
			debugLog('[SearchContext] Map geo search index:', baseIndexName);
			debugLog('[SearchContext] Geo query details:', {
				indexName: baseIndexName,
				query: query || '',
				insideBoundingBox: params.insideBoundingBox,
				mapBounds: {
					north: mapBounds.north,
					south: mapBounds.south,
					east: mapBounds.east,
					west: mapBounds.west,
				},
				facetFilters: params.facetFilters || [],
				numericFilters: params.numericFilters || [],
				hitsPerPage: params.hitsPerPage,
			});

			debugLog('🔵 [Algolia] CALLING: client.search (map geo search)', {
				indexName: baseIndexName,
				query: query || '',
				page: params.page,
				hitsPerPage: params.hitsPerPage,
				insideBoundingBox: params.insideBoundingBox,
				facetFilters: params.facetFilters || [],
				numericFilters: params.numericFilters || [],
				cacheKey: currentCacheKey,
				wasCached: false, // We already checked cache above
			});
			const searchStartTime = performance.now();
			const response = await client.search([{ indexName: baseIndexName, params }]);
			const searchEndTime = performance.now();
			let result = response?.results?.[0] ?? null;
			if (result && typeof filtersRef.current === 'object' && filtersRef.current !== null) {
				const primary = readPrimaryLocationFilterValues(filtersRef.current as Record<string, unknown>);
				if (primary.length && Array.isArray(result.hits) && result.hits.length > 0) {
					const reordered = reorderSearchHitsForPrimaryLocationBoost(
						result.hits as Array<Record<string, unknown>>,
						primary,
						locale,
					);
					if (reordered !== result.hits) {
						result = { ...result, hits: reordered };
					}
				}
			}
			
			debugLog('🟢 [Algolia] RESPONSE: client.search (map geo search)', {
				indexName: baseIndexName,
				duration: `${(searchEndTime - searchStartTime).toFixed(2)}ms`,
				nbHits: result?.nbHits || 0,
				hitsLength: result?.hits?.length || 0,
				processingTimeMS: result?.processingTimeMS || 0,
				query: result?.query || '',
			});
			
			mapCacheRef.current.set(currentCacheKey, result);
			// Only update state if results actually changed
			if (!areResultsEqual(mapResults, result)) {
				setMapResults(result);
			}
		} catch (error) {
			if (!isExpectedEnvironmentError(error)) {
				console.error('[SearchProvider] Map geo search error', error);
				setMapResults(null);
			}
		} finally {
			setIsLoading(false);
			isSearchingRef.current = false;
		}
	}, [ensureSearchClient, baseIndexName, query, locale, mapBounds, viewMode, isFinlandEnabled, mapResults, areResultsEqual]);

	const performSearch = useCallback(async () => {
		debugLog('🔵 [Algolia] performSearch ENTRY', { viewMode, query, page, sort });
		// Skip normal search if in map mode
		if (viewMode === 'map') {
			debugLog('🟡 [Algolia] performSearch SKIPPED (map mode)');
			return;
		}

		// Prevent multiple simultaneous searches
		if (isSearchingRef.current) {
			debugLog('🟡 [Algolia] performSearch SKIPPED (already searching)');
			return;
		}

		// Create a stable cache key for this search (exclude mapBounds for normal search)
		const currentCacheKey = JSON.stringify({ 
			indexName: baseIndexName, 
			query, 
			filters: filtersRef.current, 
			page, 
			sort,
			isFinlandEnabled,
			type: 'normal'
		});
		
		// Serve from cache if found
		if (cacheRef.current.has(currentCacheKey)) {
			const cachedResult = cacheRef.current.get(currentCacheKey);
			debugLog('🟡 [Algolia] CACHE HIT: normal search (skipping API call)', {
				indexName: baseIndexName,
				query: query || '',
				page,
				sort,
				cacheKey: currentCacheKey,
				nbHits: cachedResult?.nbHits || 0,
			});
			// Only update state if results actually changed (use ref to avoid stale closure)
			const currentResults = resultsRef.current;
			if (!areResultsEqual(currentResults, cachedResult)) {
				setResultsWithTracking(cachedResult);
			}
			return;
		}

		isSearchingRef.current = true;
		setIsLoading(true);

		const client = ensureSearchClient();
		if (!client) {
			isSearchingRef.current = false;
			setIsLoading(false);
			return;
		}

		try {
			const params: Record<string, any> = {
				query: query || '',
				page: Math.max(0, Number.isFinite(page) ? page : 0),
				hitsPerPage: 12,
				facets: [...FACET_ATTRIBUTES],
				clickAnalytics: true,
			};
			
			// Debug: log initial filters
			debugLog('[SearchContext] performSearch - filtersRef.current:', filtersRef.current);
			debugLog('[SearchContext] performSearch - filtersRef.current type:', typeof filtersRef.current);
			// Select index by sort preference starting from the normalized base index
			let effectiveIndexName = baseIndexName;
			if (sort === 'date') effectiveIndexName = `${baseIndexName}_postedDate_desc`;
			if (sort === 'salary') effectiveIndexName = `${baseIndexName}_salaryRangeMin_desc`;
			if (typeof filtersRef.current === 'string') {
				// Special handling: if filtering by objectID, prefer getObjects for exact match (objectID isn't facetable)
				const objectIdMatches = Array.from(filtersRef.current.matchAll(/objectID:\s*"([^"]+)"/g)).map(m => m[1]);
				if (objectIdMatches.length > 0) {
					try {
						debugLog('🔵 [Algolia] CALLING: idx.getObjects (objectID filter)', {
							indexName: effectiveIndexName,
							objectIDs: objectIdMatches,
							count: objectIdMatches.length,
						});
						const getObjectsStartTime = performance.now();
						const idx = (client as any).initIndex(effectiveIndexName);
						// @ts-ignore getObjects exists at runtime in algoliasearch/lite
						const res = await idx.getObjects(objectIdMatches);
						const getObjectsEndTime = performance.now();
						const hits = Array.isArray(res?.results) ? res.results.filter(Boolean) : [];
						
						debugLog('🟢 [Algolia] RESPONSE: idx.getObjects (objectID filter)', {
							indexName: effectiveIndexName,
							duration: `${(getObjectsEndTime - getObjectsStartTime).toFixed(2)}ms`,
							requested: objectIdMatches.length,
							returned: hits.length,
						});
						
            
						
						const result = {
							hits,
							nbHits: hits.length,
							page: 0,
							nbPages: 1,
							hitsPerPage: hits.length,
							query: query || '',
							params: {},
						};
						cacheRef.current.set(currentCacheKey, result);
						// Only update state if results actually changed (use ref to avoid stale closure)
						const currentResults = resultsRef.current;
						if (!areResultsEqual(currentResults, result)) {
							setResultsWithTracking(result);
						}
						return;
					} catch (e) {
						// Fall back to normal search if getObjects fails
					}
				}
				params.filters = filtersRef.current;
			} else if (filtersRef.current && typeof filtersRef.current === 'object' && filtersRef.current !== null && Object.keys(filtersRef.current).length > 0) {
				// Split structured filters into facetFilters and numericFilters
				const facetFilters: (string | string[])[] = [];
				const numericFilters: string[] = [];
				const numericRule: Record<string, 'gte' | 'lte'> = {
					salaryRangeMin: 'gte',
					vacationWeeks: 'gte',
					companyHeadcountRangeMax: 'lte',
					lastSeenMin: 'gte',
				};
				
				// Debug: log filters for lastSeenMin
				if ((filtersRef.current as Record<string, unknown>).lastSeenMin) {
					debugLog('[SearchContext] lastSeenMin filter found:', (filtersRef.current as Record<string, unknown>).lastSeenMin);
				}
				
				Object.entries(filtersRef.current as Record<string, unknown>).forEach(([attribute, raw]) => {
					if (isSrpNonFilterQueryParam(attribute)) {
						return;
					}
					let values = Array.isArray(raw) ? raw as Array<string | number> : [raw as string | number];
					const isExclude = attribute.startsWith('exclude_');
					const actualAttribute = isExclude ? attribute.replace('exclude_', '') : attribute;
					
					// Numeric filters cannot be excluded (they're range-based)
					if (numericRule[actualAttribute] && !isExclude) {
						values.forEach((v) => {
							// Convert to number, handling both string and number inputs
							const num = typeof v === 'number' ? v : Number(String(v).trim());
							// For posted-date window (key lastSeenMin), allow any finite number (timestamps can be large)
							// For other numeric filters, require > 0
							const isValid = actualAttribute === 'lastSeenMin' 
								? (!Number.isNaN(num) && isFinite(num))
								: (!Number.isNaN(num) && isFinite(num) && num > 0);
							if (isValid) {
								const algoliaAttribute = refinementKeyToAlgoliaNumericAttribute(actualAttribute);
								const filterString = numericRule[actualAttribute] === 'gte' 
									? `${algoliaAttribute}>=${num}` 
									: `${algoliaAttribute}<=${num}`;
								numericFilters.push(filterString);
								debugLog(`[SearchContext] Added numeric filter: ${filterString} (from ${actualAttribute}=${v})`);
							} else {
								debugLog(`[SearchContext] Failed to convert ${actualAttribute}=${v} to valid number`);
							}
						});
					} else if (!numericRule[actualAttribute]) {
						// Check if this is an exclude filter
						if (isExclude) {
							// Algolia exclude syntax: NOT attribute:value
							values.forEach((v) => {
								facetFilters.push(`NOT ${actualAttribute}:${String(v)}`);
						});
					} else {
							// Regular include filter
						if (values.length === 1) {
							facetFilters.push(`${attribute}:${String(values[0])}`);
						} else if (values.length > 1) {
							facetFilters.push(values.map((v) => `${attribute}:${String(v)}`));
							}
						}
					}
				});
				
				// Add isFinland facet filter if enabled
				if (isFinlandEnabled) {
					facetFilters.push('isFinland:true');
				}
				
				if (facetFilters.length) params.facetFilters = facetFilters;
				if (numericFilters.length) {
					params.numericFilters = numericFilters;
					debugLog('[SearchContext] Numeric filters being applied:', numericFilters);
				} else if (filtersRef.current && typeof filtersRef.current === 'object' && filtersRef.current !== null && Object.keys(filtersRef.current).length > 0 && (filtersRef.current as Record<string, unknown>).lastSeenMin) {
					debugLog('[SearchContext] lastSeenMin found in filters but not converted to numeric filter. Value:', (filtersRef.current as Record<string, unknown>).lastSeenMin);
					debugLog('[SearchContext] filtersRef.current full object:', JSON.stringify(filtersRef.current, null, 2));
				}
			} else {
				// Even if no other filters, apply isFinland if enabled
				if (isFinlandEnabled) {
					params.facetFilters = ['isFinland:true'];
				}
			}
			
			// Debug: log final params being sent to Algolia
			debugLog('[SearchContext] Final search params:', JSON.stringify(params, null, 2));
			debugLog('[SearchContext] Effective index name:', effectiveIndexName);

			debugLog('🔵 [Algolia] CALLING: client.search (normal search)', {
				indexName: effectiveIndexName,
				query: query || '',
				page: params.page,
				hitsPerPage: params.hitsPerPage,
				facetFilters: params.facetFilters || [],
				numericFilters: params.numericFilters || [],
				filters: params.filters || undefined,
				sort,
				cacheKey: currentCacheKey,
				wasCached: false, // We already checked cache above
			});
			const searchStartTime = performance.now();
			const response = await client.search([{ indexName: effectiveIndexName, params }]);
			const searchEndTime = performance.now();
			let result = response?.results?.[0] ?? null;
			if (result && typeof filtersRef.current === 'object' && filtersRef.current !== null) {
				const primary = readPrimaryLocationFilterValues(filtersRef.current as Record<string, unknown>);
				if (primary.length && Array.isArray(result.hits) && result.hits.length > 0) {
					const reordered = reorderSearchHitsForPrimaryLocationBoost(
						result.hits as Array<Record<string, unknown>>,
						primary,
						locale,
					);
					if (reordered !== result.hits) {
						result = { ...result, hits: reordered };
					}
				}
			}
			
			debugLog('🟢 [Algolia] RESPONSE: client.search (normal search)', {
				indexName: effectiveIndexName,
				duration: `${(searchEndTime - searchStartTime).toFixed(2)}ms`,
				nbHits: result?.nbHits || 0,
				hitsLength: result?.hits?.length || 0,
				page: result?.page || 0,
				nbPages: result?.nbPages || 0,
				processingTimeMS: result?.processingTimeMS || 0,
				query: result?.query || '',
			});
			
      
			
			cacheRef.current.set(currentCacheKey, result);
			// Only update state if results actually changed (use ref to avoid stale closure)
			const currentResults = resultsRef.current;
			if (!areResultsEqual(currentResults, result)) {
				setResultsWithTracking(result);
			}
			
			// Track search query with results count
			if (query && didHydrateRef.current && result) {
				// Clear old query ID if this is a new search
				clearQueryID();
				// Use Algolia's queryID from search results if available
				const algoliaQueryID = (result as any)?.queryID || (result as any)?._queryID;
				trackSearchQuery({
					query,
					locale,
					hitsCount: (result as any)?.nbHits,
					queryID: algoliaQueryID,
				});

				trackSearchAnalyticsGa4Debounced({
					search_term: query,
					search_locale: locale,
					search_result_count: (result as any)?.nbHits ?? 0,
					search_has_results: ((result as any)?.nbHits ?? 0) > 0,
					search_filters: buildSearchFiltersJson(filtersRef.current),
					search_sort: resolveSearchSortLabel(sort),
				});
			}

			// Track SRP view (debounced to avoid excessive events)
			if (didHydrateRef.current && result && typeof window !== 'undefined') {
				// Clear previous timeout
				if (srpViewTimeoutRef.current) {
					clearTimeout(srpViewTimeoutRef.current);
				}

				srpViewTimeoutRef.current = setTimeout(() => {
					const srpId = generateSrpId(query, filtersRef.current);
					
					// Only track if SRP ID changed (avoid duplicate tracking)
					if (srpId !== lastSrpIdRef.current) {
						const filtersApplied = formatFiltersApplied(filtersRef.current);
						const locationLabel = typeof filtersRef.current === 'object' && filtersRef.current && 'location' in filtersRef.current
							? Array.isArray(filtersRef.current.location) 
								? filtersRef.current.location[0] 
								: String(filtersRef.current.location)
							: undefined;

						trackEvent({
							name: 'srp_view',
							params: {
								srp_id: srpId,
								query_text: query || undefined,
								location_label: locationLabel,
								result_count: (result as any)?.nbHits,
								filters_applied: filtersApplied || undefined,
								locale: locale,
							},
						});

						lastSrpIdRef.current = srpId;
					}
				}, 500); // 500ms debounce
			}
		} catch (error) {
			if (!isExpectedEnvironmentError(error)) {
				console.error('[SearchProvider] Search error', error);
				setResultsWithTracking(null);
			}
		} finally {
			setIsLoading(false);
			isSearchingRef.current = false;
		}
	}, [ensureSearchClient, baseIndexName, page, query, sort, locale, isFinlandEnabled, viewMode, areResultsEqual]);

	// Track previous initialResults to prevent unnecessary updates
	const prevInitialResultsRef = useRef<any>(initialResults);
	
	// If initialResults exist, seed the cache for the initial key (base index + sort)
	useEffect(() => {
		// Skip cache seeding on non-search pages (but still allow state updates if needed)
		// On non-search pages, we don't need to cache results since we won't be searching
		if (!syncToUrl && !initialResults) {
			return;
		}
		
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] useEffect (initialResults) RUNNING');
		}
		// Only update if initialResults actually changed (deep comparison by checking nbHits and hits count)
		const prevResults = prevInitialResultsRef.current;
		const resultsChanged = 
			!prevResults && initialResults ? true : // Changed from null to results
			prevResults && !initialResults ? true : // Changed from results to null
			prevResults && initialResults && (
				prevResults.nbHits !== initialResults.nbHits ||
				(prevResults.hits?.length || 0) !== (initialResults.hits?.length || 0) ||
				prevResults.page !== initialResults.page
			);
		
		if (initialResults && resultsChanged) {
			// Only seed cache on search pages
			if (syncToUrl) {
				const initialKey = JSON.stringify({ indexName: baseIndexName, query: initialQuery || '', filters: initialFilters, page: initialPage, sort: initialSort });
				cacheRef.current.set(initialKey, initialResults);
			}
			// Only update state if results actually changed (compare with current results from ref to avoid dependency)
			// Use a ref to access current results without adding it to dependencies
			const currentResults = resultsRef.current;
			if (!areResultsEqual(currentResults, initialResults)) {
				setResultsWithTracking(initialResults);
			}
			setIsLoading(false);
			prevInitialResultsRef.current = initialResults;
		} else if (!initialResults && resultsChanged) {
			prevInitialResultsRef.current = null;
		}
		// Note: Removed 'results' from dependencies to prevent loop - we only want to react to initialResults changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialResults, areResultsEqual]);

	// Track previous isFinlandEnabled to detect changes
	const prevIsFinlandEnabledRef = useRef<boolean>(isFinlandEnabled);
	
	// Trigger search on state changes (but skip if results already cached)
	useEffect(() => {
		// Skip search when syncToUrl is false AND we have no filters (e.g. job detail / generic non-search pages).
		// Tools pages use syncToUrl=false but pass initialFilters (objectID: "id1" OR ...); they must still run search.
		const hasFilters =
			(typeof initialFilters === 'string' && initialFilters.length > 0) ||
			(initialFilters && typeof initialFilters === 'object' && Object.keys(initialFilters as object).length > 0);
		if (!syncToUrl && !hasFilters) {
			if (typeof window !== 'undefined') {
				debugLog('[SearchContext] Search effect SKIPPED (non-search page, syncToUrl=false, no filters)');
			}
			return;
		}
		
		debugLog('[SearchContext] Search effect running', { 
			viewMode, 
			hasInitialResults: !!initialResults, 
			didHydrate: didHydrateRef.current,
			hasMapBounds: !!mapBounds
		});
		
		// Skip initial search if we have SSR results and this is the initial state
		// In map mode, if we have SSR geo results, use them instead of searching
		if (initialResults && !ssrInitialSearchHandledRef.current) {
			ssrInitialSearchHandledRef.current = true;
			prevIsFinlandEnabledRef.current = isFinlandEnabled;
			
			// If in map mode and we have initial results, set them as map results
			if (viewMode === 'map' && initialResults) {
				debugLog('[SearchContext] Using SSR geo results for map mode', { nbHits: initialResults.nbHits });
				// Set map results from SSR initial results immediately
				setMapResults(initialResults);
				// Cache the SSR results
				const cacheKey = JSON.stringify({
					indexName: baseIndexName,
					query,
					filters: filtersRef.current,
					mapBounds: null, // SSR results use default bounds
					isFinlandEnabled,
					type: 'map'
				});
				mapCacheRef.current.set(cacheKey, initialResults);
			}
			
			// For non-map mode, skip the search since we have SSR results
			if (viewMode !== 'map') {
				return;
			}
			// For map mode, we still want to search when bounds change, but we have initial results
		}
		
		// If isFinlandEnabled changed, clear cache entries that don't match the new state
		if (prevIsFinlandEnabledRef.current !== isFinlandEnabled) {
			// Clear all cache entries to force fresh search
			cacheRef.current.clear();
			mapCacheRef.current.clear();
			prevIsFinlandEnabledRef.current = isFinlandEnabled;
		}
		
		const timeoutId = setTimeout(() => {
			debugLog('🔵 [SearchContext] Triggering search', { viewMode, hasMapBounds: !!mapBounds, query, page, sort, filtersVersion });
			// Use refs to avoid including callbacks in dependencies (they're stable but references change)
			const currentPerformSearch = performSearchRef.current;
			const currentPerformMapSearch = performMapSearchRef.current;
			if (viewMode === 'map') {
				// Use geo search for map mode
				debugLog('🔵 [SearchContext] Calling performMapSearch');
				currentPerformMapSearch();
			} else {
				// Use normal search for grid/list mode
				debugLog('🔵 [SearchContext] Calling performSearch');
				currentPerformSearch();
			}
		}, 0);
		
		return () => clearTimeout(timeoutId);
		// Removed performSearch and performMapSearch from dependencies - using refs instead to prevent re-runs when callbacks are recreated
	}, [baseIndexName, page, query, sort, filtersVersion, mapBounds, viewMode, isFinlandEnabled, syncToUrl]);

  // React to changes in initialFilters prop (from parent pages) by updating filtersRef
  // Only update if filters actually changed (by content key, not just string representation)
  useEffect(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] useEffect (initialFilters) RUNNING');
		}
    const nextKey = filtersKey(initialFilters);
    if (nextKey !== filtersKeyRef.current) {
      if (
        typeof window !== 'undefined' &&
        shouldIgnoreEmptyInitialFiltersFromLayout({
          syncToUrl,
          nextFiltersKey: nextKey,
          windowHref: window.location.href,
        })
      ) {
        debugLog(
          '[SearchContext] Ignoring stale empty initialFilters from layout (URL still encodes refinements)',
          { prevKey: filtersKeyRef.current, nextKey },
        );
        return;
      }
      // Filters actually changed - update refs and version
      if (typeof window !== 'undefined') {
        debugLog('🔴 [SearchContext] initialFilters changed', {
          prevKey: filtersKeyRef.current,
          nextKey,
        });
      }
      filtersKeyRef.current = nextKey;
      filtersRef.current = initialFilters;
      setFiltersVersion((v) => v + 1);
    } else if (typeof window !== 'undefined') {
      debugLog('🟢 [SearchContext] initialFilters unchanged (skipping update)', {
        filtersKey: nextKey,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters, syncToUrl]);

    // Reset page to 0 when query/filters/sort change AFTER initial hydration
    useEffect(() => {
		// Skip on non-search pages
		if (!syncToUrl) return;
		
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] useEffect (query/sort/filtersVersion sync) RUNNING');
		}
        // If this change originated from a URL-driven update, don't force-reset page
        if (suppressUrlSyncRef.current) {
            return;
        }
        const currentFiltersStr = JSON.stringify(filtersRef.current || {});
        const queryChanged = prevQueryRef.current !== query;
        const filtersChanged = prevFiltersRef.current !== currentFiltersStr;
        const sortChanged = prevSortRef.current !== sort;
        if (didHydrateRef.current && (queryChanged || filtersChanged || sortChanged)) {
            setPage(0);
            // Also sync URL immediately so page param resets alongside state
            try {
                if (typeof window !== 'undefined') {
                    const uiState: any = {
                        query: query || '',
                        refinementList: typeof filtersRef.current === 'object' && filtersRef.current ? filtersRef.current : {},
                        page: 0, // 0-based: page 0 = first page
                        sort,
                        isFinland: isFinlandEnabled,
                    };
                    const url = generateUrlFromIndexUiState(
                      uiState,
                      baseIndexName,
                      window.location.pathname,
                      urlSyncOptionsForPathname(window.location.pathname),
                    );
                    const targetUrl = url && url.length > 0 ? url : window.location.pathname;
                    // Prevent URL sync effect from writing back immediately
                    suppressUrlSyncRef.current = true;
                    window.history.replaceState({}, '', targetUrl);
                    // Ensure any listeners react (in case history patch not yet applied)
                    try { window.dispatchEvent(new Event('urlchange')); } catch {}
                }
            } catch {}
        }
        // Don't update refs here - they're updated in the URL sync effect
        // This ensures the URL sync effect can detect changes before refs are updated
    }, [query, sort, filtersVersion, syncToUrl, baseIndexName, isFinlandEnabled]);

	// Track last saved URL to avoid saving duplicates
	const lastSavedUrlRef = useRef<string | null>(null);
	const hasSavedInitialSearchRef = useRef<boolean>(false);

	/**
	 * Saves current search to "saved searches" (recent list) when:
	 * - Redux persist has rehydrated (isRehydrated), and
	 * - We have a meaningful search (non-empty query or any filters), and
	 * - The URL is different from the last saved.
	 *
	 * Triggers (all inside the URL-sync effect, client-only, syncToUrl true):
	 * 1. Initial load: first run after rehydration, if page has query or filters (e.g. landed on a search URL).
	 * 2. Suppress branch: user just changed query/filters/sort; the other effect set suppressUrlSyncRef and updated the URL, we save here.
	 * 3. Normal path: we're syncing the URL and isSearchChange && page === 0 && targetUrl !== lastSaved.
	 */
	function trySaveCurrentSearchToRecent(filters: Record<string, unknown>, opts: { markInitial?: boolean }) {
		const hasQuery = query && String(query).trim().length > 0;
		const hasFilters = Object.keys(filters || {}).length > 0;
		if (!hasQuery && !hasFilters) return;
		try {
			const uiState: any = { query: query || '', refinementList: filters || {}, page: 0, sort, isFinland: isFinlandEnabled };
			const url = generateUrlFromIndexUiState(
				uiState,
				baseIndexName,
				window.location.pathname,
				urlSyncOptionsForPathname(window.location.pathname),
			);
			const targetUrl = url && url.length > 0 ? url : window.location.pathname;
			const fullUrl = normalizeSavedSearchUrlInput(window.location.origin + targetUrl);
			if (shouldSkipPersistingSavedSearchUrl(fullUrl)) return;
			if (fullUrl === lastSavedUrlRef.current) return;
			dispatch(addSearch(fullUrl));
			lastSavedUrlRef.current = fullUrl;
			if (opts.markInitial) hasSavedInitialSearchRef.current = true;
		} catch (e) {
			debugLog('[SearchContext] Failed to save recent search:', e);
		}
	}
	
	// Sync URL on state changes (client only)
	useEffect(() => {
		if (!syncToUrl || typeof window === 'undefined') return;
		// Wait for redux-persist rehydration so we don't get overwritten by REHYDRATE
		if (!isRehydrated) return;

		const currentFilters = typeof filtersRef.current === 'object' && filtersRef.current ? filtersRef.current : {};

		// First run after hydration: save initial search if page has query or filters
		if (!didHydrateRef.current) {
			didHydrateRef.current = true;
			trySaveCurrentSearchToRecent(currentFilters as Record<string, unknown>, { markInitial: true });
			return;
		}

		// User just changed query/filters/sort (other effect set suppressUrlSyncRef and updated URL)
		if (suppressUrlSyncRef.current) {
			suppressUrlSyncRef.current = false;
			trySaveCurrentSearchToRecent(currentFilters as Record<string, unknown>, {});
			return;
		}
		
		const currentFiltersStr = JSON.stringify(currentFilters);
		const queryChanged = prevQueryRef.current !== query;
		const filtersChanged = prevFiltersRef.current !== currentFiltersStr;
		const sortChanged = prevSortRef.current !== sort;
		const isSearchChange = queryChanged || filtersChanged || sortChanged;
		
		try {
			if ((currentFilters as Record<string, unknown>).lastSeenMin) {
				debugLog('[SearchContext] URL sync - lastSeenMin found in filtersRef.current:', (currentFilters as Record<string, unknown>).lastSeenMin);
				debugLog('[SearchContext] URL sync - full filtersRef.current:', JSON.stringify(currentFilters, null, 2));
			}
			const uiState: any = {
				query: query || '',
				refinementList: currentFilters,
				page: Number.isFinite(page) ? page : 0,
				sort,
				isFinland: isFinlandEnabled,
			};
			const url = generateUrlFromIndexUiState(
				uiState,
				baseIndexName,
				window.location.pathname,
				urlSyncOptionsForPathname(window.location.pathname),
			);
			if ((currentFilters as Record<string, unknown>).lastSeenMin && !url.includes('lastSeenMin')) {
				debugLog('[SearchContext] URL sync - lastSeenMin was in filters but not in generated URL:', url);
			}
			if (typeof window !== 'undefined' && typeof window.history !== 'undefined') {
				const targetUrl = url && url.length > 0 ? url : window.location.pathname;
				window.history.replaceState({}, '', targetUrl);
				window.dispatchEvent(new Event('urlchange'));
				if (isSearchChange && page === 0) {
					trySaveCurrentSearchToRecent(currentFilters as Record<string, unknown>, {});
				}
			}
			prevQueryRef.current = query;
			prevFiltersRef.current = currentFiltersStr;
			prevSortRef.current = sort;
		} catch (e) {
			try {
				const uiState: any = {
					query: query || '',
					refinementList: typeof filtersRef.current === 'object' && filtersRef.current ? filtersRef.current : {},
					page: Number.isFinite(page) ? page : 0,
					sort,
					isFinland: isFinlandEnabled,
				};
				const url =
					generateUrlFromIndexUiState(
						uiState,
						baseIndexName,
						window.location.pathname,
						urlSyncOptionsForPathname(window.location.pathname),
					) || (typeof window !== 'undefined' ? window.location.pathname : '');
				if (typeof window !== 'undefined' && typeof window.history !== 'undefined') {
					window.history.pushState({}, '', url);
				}
			} catch {}
		}
	}, [syncToUrl, query, page, baseIndexName, filtersVersion, sort, dispatch, isRehydrated, isFinlandEnabled]);

	// React to URL changes by updating local state (popstate, SPA navigations)
	useEffect(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] useEffect (URL sync) RUNNING');
		}
		if (typeof window === 'undefined') return;
		// Respect pages that do not want URL-driven state
		if (!syncToUrl) return;
		// Patch history methods once to emit a custom event on SPA navigations
		if (!(history as any)._patchedForSearchProvider) {
			const origPush = history.pushState;
			const origReplace = history.replaceState;
			(history as any).pushState = function(this: History, ...args: any[]) {
				const ret = (origPush as any).apply(this, args as any);
				window.dispatchEvent(new Event('urlchange'));
				return ret;
			};
			(history as any).replaceState = function(this: History, ...args: any[]) {
				const ret = (origReplace as any).apply(this, args as any);
				window.dispatchEvent(new Event('urlchange'));
				return ret;
			};
			(history as any)._patchedForSearchProvider = true;
		}

		const handleUrl = () => {
			try {
				// Skip URL handling during initial hydration if we have SSR data
				// This prevents the URL parser from overriding SSR data
				if (initialResults && !didHydrateRef.current) {
					return;
				}
				
				const ui = parseUrlToIndexUiState(window.location.href);
				const nextQuery = ui.query || '';
				// URL uses 0-based indexing (page 0 = first page, page 1 = second page, etc.)
				const nextPage = Math.max(0, ui.page !== undefined && ui.page !== null ? ui.page : 0);
				const nextFilters = (ui.refinementList || {}) as Record<string, string[]>;
				// Debug: log if lastSeenMin is in URL
				if (nextFilters.lastSeenMin) {
					debugLog('[SearchContext] handleUrl - lastSeenMin found in parsed URL:', nextFilters.lastSeenMin);
					debugLog('[SearchContext] handleUrl - full nextFilters:', JSON.stringify(nextFilters, null, 2));
				}
				const parsedSort = (ui as any)?.sort as 'relevance' | 'date' | 'salary' | undefined;
				const nextSort: 'relevance' | 'date' | 'salary' =
					parsedSort === 'date' || parsedSort === 'salary' || parsedSort === 'relevance'
						? parsedSort
						: 'relevance';
				const nextIsFinland = (ui as any).isFinland !== false;
				if ((ui as any).isFinland !== undefined && nextIsFinland !== isFinlandEnabled) {
					dispatch(setFinlandFilterEnabled(nextIsFinland));
				}
				// Only apply URL-derived state if it's different from current state
				// This prevents unnecessary state changes during initial hydration
				const currentQuery = query;
				const currentPage = page;
				const currentFiltersStr = JSON.stringify(filtersRef.current || {});
				const nextFiltersStr = JSON.stringify(nextFilters);
				const currentSort = sort;
				
				
				if (currentQuery !== nextQuery || currentPage !== nextPage || currentFiltersStr !== nextFiltersStr || currentSort !== nextSort) {
					suppressUrlSyncRef.current = true;
					setQuery(nextQuery);
					setPage(nextPage);
					filtersRef.current = nextFilters;
					setFiltersVersion((v) => v + 1);
					setSort(nextSort);
				}
			} catch {}
		};

		window.addEventListener('popstate', handleUrl);
		window.addEventListener('urlchange', handleUrl);
		// Also run once on mount to reflect current URL
		handleUrl();
		return () => {
			window.removeEventListener('popstate', handleUrl);
			window.removeEventListener('urlchange', handleUrl);
		};
	}, [query, page, sort, syncToUrl, isFinlandEnabled, dispatch]);

	// Client-side hydration diagnostics (does not affect markup)
	useEffect(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] useEffect (hydration diagnostics) RUNNING');
		}
		if (typeof window === 'undefined') return;
		try {
			const parsed = parseUrlToIndexUiState(window.location.href) as any;
			const summary = {
				initialProps: initialPropsRef.current,
				urlUiState: { query: parsed.query, page: parsed.page, refinementList: parsed.refinementList },
				clientState: { query, page, filters: filtersRef.current },
			initialResultsNbHits: (initialResults as any)?.nbHits,
			currentResultsNbHits: (effectiveResults as any)?.nbHits,
		};
	} catch {}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

  const setSortWithTracking = useCallback((newSort: 'relevance' | 'date' | 'salary') => {
		trackSortChange({
			sortType: newSort,
			locale,
		});
		setSort(newSort);
	}, [locale]);

  // Extract stable values from pageContext to prevent unnecessary recalculations
  const pageContextPathname = pageContext?.urlPathname;
  const pageContextOriginal = pageContext?.urlOriginal;
  
  // Normalize pathname to always include locale prefix for consistency between SSR and client
  // This must be the same on both server and client to prevent hydration mismatches
  // Store SSR pathname in ref during SSR, use it during client hydration
  // Use a ref to store the normalized pathname after initial hydration to prevent unnecessary recalculations
  const normalizedPathnameRef = useRef<string | null>(null);
  const normalizedPathname = useMemo(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] normalizedPathname useMemo RUNNING');
		}
		// After initial hydration, use the stored value to prevent recalculation
		// Only recalculate if the actual pathname changed
		const currentPathname = pageContextPathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
		if (normalizedPathnameRef.current && didHydrateRef.current && normalizedPathnameRef.current.endsWith(currentPathname)) {
			return normalizedPathnameRef.current;
		}
		
		let rawPathname = '/';
		
		// On server, compute and store the normalized pathname
		if (typeof window === 'undefined') {
			if (pageContextPathname) {
				rawPathname = pageContextPathname;
			} else if (pageContextOriginal) {
				try {
					const url = new URL(pageContextOriginal, 'http://localhost');
					rawPathname = url.pathname;
				} catch {
					const match = String(pageContextOriginal).match(/^[^?]*/);
					rawPathname = match ? match[0] : '/';
				}
			}
			
			// Normalize and store for hydration
			const pathSegments = rawPathname.split('/').filter(Boolean);
			const firstSegment = pathSegments[0]?.toLowerCase();
			const hasLocalePrefix = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);
			
			if (!hasLocalePrefix && locale) {
				rawPathname = `/${locale}${rawPathname === '/' ? '' : rawPathname}`;
			}
			
			ssrPathnameRef.current = rawPathname;
			normalizedPathnameRef.current = rawPathname;
			return rawPathname;
		}
		
		// On client during initial hydration, use stored SSR pathname to ensure consistency
		// After hydration completes, we can use the current pathname
		if (ssrPathnameRef.current && !didHydrateRef.current) {
			normalizedPathnameRef.current = ssrPathnameRef.current;
			return ssrPathnameRef.current;
		}
		
		// After hydration, use pageContext or window location (only calculate once)
		if (pageContextPathname) {
			rawPathname = pageContextPathname;
		} else if (typeof window !== 'undefined') {
			rawPathname = window.location.pathname;
		}
		
		// Normalize pathname to always include locale prefix
		const pathSegments = rawPathname.split('/').filter(Boolean);
		const firstSegment = pathSegments[0]?.toLowerCase();
		const hasLocalePrefix = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);
		
		if (!hasLocalePrefix && locale) {
			rawPathname = `/${locale}${rawPathname === '/' ? '' : rawPathname}`;
		}
		
		// Store for future renders
		normalizedPathnameRef.current = rawPathname;
		return rawPathname;
	}, [
		pageContextPathname, // Primary source (consistent on both server and client)
		pageContextOriginal, // Server fallback
		locale,
	]);

  // Generate URL for a specific page number
  const getPageUrl = useCallback((targetPage: number): string => {
		try {
			const uiState: any = {
				query: query || '',
				refinementList: typeof filtersRef.current === 'object' && filtersRef.current ? filtersRef.current : {},
				page: targetPage, // Use 0-based for URLs (page 0 = first page, page 1 = second page, etc.)
				sort,
				isFinland: isFinlandEnabled,
			};
			
			// Use normalized pathname that's consistent between SSR and client
			const url = generateUrlFromIndexUiState(
				uiState,
				baseIndexName,
				normalizedPathname,
				urlSyncOptionsForPathname(normalizedPathname),
			);
			return url || '#';
		} catch (e) {
			return '#';
		}
	}, [query, sort, baseIndexName, normalizedPathname]);

  // Memoize filters object to avoid recreating it on every render
  // Use a stable empty object to prevent reference changes
  const emptyFiltersObject = useMemo(() => ({}), []);
  const filtersObject = useMemo(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] filtersObject useMemo RUNNING');
		}
		return (typeof filtersRef.current === 'object' && filtersRef.current) ? filtersRef.current : emptyFiltersObject;
	}, [filtersVersion, emptyFiltersObject]);
	
	// Track previous values to identify what's triggering useMemo recalculation
	// Include ALL dependencies from the useMemo array
	const prevDepsForTrackingRef = useRef<{
		query?: string;
		page?: number;
		sort?: string;
		viewMode?: string;
		resultsVersion?: number;
		filtersObject?: any;
		initialResults?: any;
		mapResults?: any;
		areResultsEqual?: any;
	}>({});

  // Memoize setFilters callback to avoid recreating it on every render
  const setFiltersCallback = useCallback((newFilters: AlgoliaFilters) => {
		// Capture call stack to identify who's calling setFilters
		if (typeof window !== 'undefined') {
			try {
				const stack = new Error().stack;
				const stackLines = stack?.split('\n').slice(2, 8) || [];
				setFiltersCallStackRef.current = stackLines.map(line => line.trim());
			} catch (e) {
				// Ignore stack trace errors
			}
		}
		
		// Check if filters actually changed (by content, not just reference)
		const nextKey = filtersKey(newFilters);
		if (nextKey === filtersKeyRef.current) {
			// No semantic change → do nothing
			if (typeof window !== 'undefined') {
				debugLog('🟢 [SearchContext] setFiltersCallback called but filters unchanged (skipping update)', {
					filtersKey: nextKey,
					callStack: setFiltersCallStackRef.current.slice(-3),
				});
			}
			return;
		}

		// Only now consider it a real update
		if (typeof window !== 'undefined') {
			debugLog('🔴 [SearchContext] setFiltersCallback - filters changed', {
				prevKey: filtersKeyRef.current,
				nextKey,
				callStack: setFiltersCallStackRef.current.slice(-3),
			});
		}
		
		// Track filter changes
		const oldFilters = filtersRef.current;
		const oldFiltersObj = typeof oldFilters === 'object' && oldFilters ? oldFilters : {};
		const newFiltersObj = typeof newFilters === 'object' && newFilters ? newFilters : {};
		
		// Track added filters
		Object.entries(newFiltersObj).forEach(([key, values]) => {
			const oldValues = oldFiltersObj[key];
			const newValues = Array.isArray(values) ? values : [values];
			const oldValuesArray = Array.isArray(oldValues) ? oldValues : oldValues ? [oldValues] : [];
			
			newValues.forEach(value => {
				if (!oldValuesArray.includes(value as string)) {
					trackFilterAdd({
						filterKey: key,
						filterValue: String(value),
						locale,
					});
				}
			});
		});
		
		// Track removed filters
		Object.entries(oldFiltersObj).forEach(([key, values]) => {
			const newValues = newFiltersObj[key];
			const oldValuesArray = Array.isArray(values) ? values : [values];
			const newValuesArray = Array.isArray(newValues) ? newValues : newValues ? [newValues] : [];
			
			oldValuesArray.forEach(value => {
				if (!newValuesArray.includes(value as string)) {
					trackFilterRemove({
						filterKey: key,
						filterValue: String(value),
						locale,
					});
				}
			});
		});
		
		// Update filters and version only when content actually changed
		filtersKeyRef.current = nextKey;
		filtersRef.current = newFilters;
		setFiltersVersion((v) => v + 1);
		// Let the URL sync effect handle URL updates
		// Kick off search immediately
		void performSearch();
	}, [locale, performSearch]);

	// Update callback refs after all callbacks are defined
	performSearchRef.current = performSearch;
	performMapSearchRef.current = performMapSearch;
	setFiltersCallbackRef.current = setFiltersCallback;
	setSortWithTrackingRef.current = setSortWithTracking;
	getPageUrlRef.current = getPageUrl;

  // ===== ISOLATE DEPENDENCIES INTO SEPARATE useMemo HOOKS =====
  // This allows us to see exactly which dependency is changing
  
  // Track basic state (query, page, sort, viewMode)
  const basicStatePrevRef = useRef<{ query?: string; page?: number; sort?: string; viewMode?: string }>({});
  const basicState = useMemo(() => {
    if (typeof window !== 'undefined') {
      debugLog('🪝 [SearchContext] basicState useMemo RUNNING');
    }
    const prev = basicStatePrevRef.current;
    const isFirstCalc = prev.query === undefined;
    
    if (!isFirstCalc && typeof window !== 'undefined') {
      const changes: string[] = [];
      if (prev.query !== query) changes.push(`query: "${prev.query}" -> "${query}"`);
      if (prev.page !== page) changes.push(`page: ${prev.page} -> ${page}`);
      if (prev.sort !== sort) changes.push(`sort: "${prev.sort}" -> "${sort}"`);
      if (prev.viewMode !== viewMode) changes.push(`viewMode: "${prev.viewMode}" -> "${viewMode}"`);
      if (changes.length > 0) {
        debugLog('🔵 [SearchContext] basicState useMemo recalculated:', changes.join(', '));
      }
    }
    
    basicStatePrevRef.current = { query, page, sort, viewMode };
    return { query, page, sort, viewMode };
  }, [query, page, sort, viewMode]);
  
  // Track resultsVersion
  const resultsVersionPrevRef = useRef<number | undefined>(undefined);
  const resultsVersionMemo = useMemo(() => {
    if (typeof window !== 'undefined') {
      debugLog('🪝 [SearchContext] resultsVersionMemo useMemo RUNNING');
    }
    const prev = resultsVersionPrevRef.current;
    const isFirstCalc = prev === undefined;
    
    if (!isFirstCalc && typeof window !== 'undefined') {
      if (prev !== resultsVersion) {
        debugLog(`🔵 [SearchContext] resultsVersion useMemo recalculated: ${prev} -> ${resultsVersion}`);
      }
    }
    
    resultsVersionPrevRef.current = resultsVersion;
    return resultsVersion;
  }, [resultsVersion]);
  
  // Track filtersObject
  const filtersObjectPrevRef = useRef<any>(undefined);
  const filtersObjectMemo = useMemo(() => {
    if (typeof window !== 'undefined') {
      debugLog('🪝 [SearchContext] filtersObjectMemo useMemo RUNNING');
    }
    const prev = filtersObjectPrevRef.current;
    const isFirstCalc = prev === undefined;
    
    if (!isFirstCalc && typeof window !== 'undefined') {
      if (prev !== filtersObject) {
        debugLog('🔵 [SearchContext] filtersObject useMemo recalculated (ref changed)');
      }
    }
    
    filtersObjectPrevRef.current = filtersObject;
    return filtersObject;
  }, [filtersObject]);
  
  // Track initialResults
  const initialResultsPrevRef = useRef<any>(undefined);
  const initialResultsMemo = useMemo(() => {
    if (typeof window !== 'undefined') {
      debugLog('🪝 [SearchContext] initialResultsMemo useMemo RUNNING');
    }
    const prev = initialResultsPrevRef.current;
    const isFirstCalc = prev === undefined;
    
    if (!isFirstCalc && typeof window !== 'undefined') {
      if (prev !== initialResults) {
        debugLog('🔵 [SearchContext] initialResults useMemo recalculated (ref changed)');
      }
    }
    
    initialResultsPrevRef.current = initialResults;
    return initialResults;
  }, [initialResults]);
  
  // Track mapResults
  const mapResultsPrevRef = useRef<any>(undefined);
  const mapResultsMemo = useMemo(() => {
    if (typeof window !== 'undefined') {
      debugLog('🪝 [SearchContext] mapResultsMemo useMemo RUNNING');
    }
    const prev = mapResultsPrevRef.current;
    const isFirstCalc = prev === undefined;
    
    if (!isFirstCalc && typeof window !== 'undefined') {
      if (prev !== mapResults) {
        debugLog('🔵 [SearchContext] mapResults useMemo recalculated (ref changed)');
      }
    }
    
    mapResultsPrevRef.current = mapResults;
    return mapResults;
  }, [mapResults]);
  
  // Track areResultsEqual
  const areResultsEqualPrevRef = useRef<any>(undefined);
  const areResultsEqualMemo = useMemo(() => {
    if (typeof window !== 'undefined') {
      debugLog('🪝 [SearchContext] areResultsEqualMemo useMemo RUNNING');
    }
    const prev = areResultsEqualPrevRef.current;
    const isFirstCalc = prev === undefined;
    
    if (!isFirstCalc && typeof window !== 'undefined') {
      if (prev !== areResultsEqual) {
        debugLog('🔵 [SearchContext] areResultsEqual useMemo recalculated (ref changed)');
      }
    }
    
    areResultsEqualPrevRef.current = areResultsEqual;
    return areResultsEqual;
  }, [areResultsEqual]);

  // Use a ref to store the previous context value and only create a new one if values actually changed
  const prevValueRef = useRef<SearchContextValue | null>(null);
  const prevDepsRef = useRef<Record<string, any>>({});
  // Track previous resultsVersion to detect when it changes (must be outside useMemo)
  const prevResultsVersionRef = useRef<number>(0);
  const value = useMemo<SearchContextValue>(() => {
		if (typeof window !== 'undefined') {
			debugLog('🪝 [SearchContext] value useMemo RUNNING');
		}
		// ===== INPUT TRACKING =====
		const isFirstCalc = !prevValueRef.current;
		
		if (!isFirstCalc && typeof window !== 'undefined') {
			debugLog('📥 [SearchContext] value useMemo RECALCULATING (one of the isolated dependencies changed)');
		}
		
		// Compute effectiveResults inside this useMemo to avoid dependency on the effectiveResults useMemo
		// This prevents unnecessary recalculations when results/initialResults change but are effectively equal
		// Use the memoized initialResults
		const currentResults = results || initialResultsMemo;
		const previousResults = stableResultsRef.current;
		
		// Check if resultsVersion changed BEFORE computing stableEffectiveResults
		// This ensures we always use new results when version changes (e.g., after filter changes)
		const resultsVersionChanged = prevResultsVersionRef.current !== resultsVersionMemo;
		
		let stableEffectiveResults = previousResults;
		
		// If resultsVersion changed, always use the new results (even if content appears same)
		// This is critical for filter changes - we need to trigger re-renders even if results look similar
		if (resultsVersionChanged) {
			stableResultsRef.current = currentResults;
			stableEffectiveResults = currentResults;
			if (typeof window !== 'undefined') {
				debugLog('[SearchContext] Results version changed - forcing new results reference', {
					prevNbHits: previousResults?.nbHits,
					currNbHits: currentResults?.nbHits,
					prevVersion: prevResultsVersionRef.current,
					currVersion: resultsVersionMemo,
				});
			}
		} else {
			// Only update the stable reference if results actually changed
			// This is critical - we want to keep the same reference to prevent unnecessary re-renders
			// Use the memoized areResultsEqual
			if (!areResultsEqualMemo(previousResults, currentResults)) {
				// Content actually changed - update the stable reference
				stableResultsRef.current = currentResults;
				stableEffectiveResults = currentResults;
				if (typeof window !== 'undefined') {
					debugLog('[SearchContext] Results content changed - updating stable reference', {
						prevNbHits: previousResults?.nbHits,
						currNbHits: currentResults?.nbHits,
					});
				}
			} else {
				// Content is the same - keep using the previous stable reference to prevent re-renders
				// Even if currentResults is a new reference, we use previousResults (stableResultsRef.current)
				stableEffectiveResults = previousResults;
				// Update the ref to the new reference for future comparisons, but don't use it in context value
				if (previousResults !== currentResults && currentResults !== null) {
					stableResultsRef.current = currentResults;
					// Only log if this happens on an UPDATE (not initial render)
					if (typeof window !== 'undefined' && previousResults !== null) {
						debugLog('🔴 [SearchContext] Results ref changed but content same - keeping previous stable reference');
					}
				}
			}
		}
		
		// Track what dependencies changed for debugging (include everything that could change)
		const currentDeps = {
			query,
			filtersObject,
			page,
			effectiveResults: stableEffectiveResults,
			sort,
			viewMode,
			// Also track callbacks to see if they're changing
			performSearch,
			setFiltersCallback,
			setSortWithTracking,
			getPageUrl,
			setQuery,
			setPage,
			setMapBounds,
			setViewMode,
		};
		
		const changedDeps: string[] = [];
		const changedDetails: Record<string, { prev: any; curr: any; type: string }> = {};
		const isFirstRender = !prevDepsRef.current;
		
		if (prevDepsRef.current) {
			Object.keys(currentDeps).forEach(key => {
				const prev = prevDepsRef.current[key];
				const curr = currentDeps[key];
				
				// Skip comparison if previous value is undefined (indicates first render with uninitialized values)
				// This prevents false positives when React StrictMode causes double renders
				if (prev === undefined) {
					return;
				}
				
				// Use deep equality for objects to avoid false positives from reference changes
				let isChanged = prev !== curr;
				if (isChanged && typeof prev === 'object' && typeof curr === 'object' && prev !== null && curr !== null) {
					// For filtersObject, compare keys and values
					if (key === 'filtersObject') {
						const prevKeys = Object.keys(prev);
						const currKeys = Object.keys(curr);
						if (prevKeys.length === currKeys.length) {
							isChanged = prevKeys.some(k => prev[k] !== curr[k]);
						}
					}
					// For effectiveResults, use areResultsEqual if available
					else if (key === 'effectiveResults' && areResultsEqualMemo) {
						isChanged = !areResultsEqualMemo(prev, curr);
					}
				}
				
				if (isChanged) {
					changedDeps.push(key);
					
					// Get more details about what changed
					let prevStr = String(prev);
					let currStr = String(curr);
					
					// For objects/arrays, show more detail
					if (typeof prev === 'object' && prev !== null) {
						try {
							prevStr = JSON.stringify(prev).slice(0, 100);
						} catch {}
					}
					if (typeof curr === 'object' && curr !== null) {
						try {
							currStr = JSON.stringify(curr).slice(0, 100);
						} catch {}
					}
					
					// For functions, show if they're the same function
					const prevIsFunc = typeof prev === 'function';
					const currIsFunc = typeof curr === 'function';
					
					changedDetails[key] = {
						prev: prevIsFunc ? `[Function]` : (prevStr.length > 50 ? prevStr.slice(0, 50) + '...' : prevStr),
						curr: currIsFunc ? `[Function]` : (currStr.length > 50 ? currStr.slice(0, 50) + '...' : currStr),
						type: prevIsFunc && currIsFunc ? 'function' : typeof prev,
					};
					
					// Special handling for results
					if (key === 'effectiveResults') {
						const prevHits = (prev as any)?.hits?.length ?? 0;
						const currHits = (curr as any)?.hits?.length ?? 0;
						const prevNbHits = (prev as any)?.nbHits ?? 0;
						const currNbHits = (curr as any)?.nbHits ?? 0;
						changedDetails[key].prev = `nbHits: ${prevNbHits}, hits: ${prevHits}`;
						changedDetails[key].curr = `nbHits: ${currNbHits}, hits: ${currHits}`;
					}
					
					// Special handling for filtersObject
					if (key === 'filtersObject') {
						const prevKeys = Object.keys(prev || {}).length;
						const currKeys = Object.keys(curr || {}).length;
						changedDetails[key].prev = `keys: ${prevKeys}`;
						changedDetails[key].curr = `keys: ${currKeys}`;
					}
				}
			});
		}
		
		// Only log meaningful changes (exclude callbacks)
		const meaningfulChanges = changedDeps.filter(dep => 
			!['performSearch', 'setFiltersCallback', 'setSortWithTracking', 'getPageUrl', 'setQuery', 'setPage', 'setMapBounds', 'setViewMode'].includes(dep)
		);
		
		// Skip logging if this is the first render or if we're comparing against undefined values
		// (which indicates the previous render had uninitialized values - common with React StrictMode)
		const hasUndefinedPrev = prevDepsRef.current && meaningfulChanges.some(dep => {
			const prev = prevDepsRef.current[dep];
			return prev === undefined;
		});
		const shouldLog = !isFirstRender && !hasUndefinedPrev && meaningfulChanges.length > 0;
		
		if (shouldLog && typeof window !== 'undefined') {
			debugLog('🔵 [SearchContext] Dependencies changed:', meaningfulChanges.join(', '), {
				details: Object.fromEntries(meaningfulChanges.map(k => [k, changedDetails[k]])),
			});
		}
		
		// Update prevDepsRef for next comparison
		// Always update it, but we've already added checks above to skip logging when prev is undefined
		prevDepsRef.current = currentDeps;
		
		// Read callbacks from refs to avoid including them in dependencies
		const currentPerformSearch = performSearchRef.current || performSearch;
		const currentPerformMapSearch = performMapSearchRef.current || performMapSearch;
		const currentSetFiltersCallback = setFiltersCallbackRef.current || setFiltersCallback;
		const currentSetSortWithTracking = setSortWithTrackingRef.current || setSortWithTracking;
		const currentGetPageUrl = getPageUrlRef.current || getPageUrl;
		
		// Use mapResults when in map mode, otherwise use normal results
		// Use the memoized values to ensure we're using stable references
		const effectiveResultsForContext = basicState.viewMode === 'map' ? mapResultsMemo : stableEffectiveResults;
		const refetchFunction = viewMode === 'map' ? currentPerformMapSearch : currentPerformSearch;
		
		const newValue: SearchContextValue = {
			// Removed indexName - not used by consumers
			query: basicState.query,
			setQuery,
			filters: filtersObjectMemo,
			setFilters: currentSetFiltersCallback,
			page: basicState.page,
			setPage,
			results: effectiveResultsForContext,
			initialResults: initialResultsMemo ?? null,
			isSearchLoading: isLoading,
			refetch: refetchFunction,
			sort: basicState.sort,
			setSort: currentSetSortWithTracking,
			getPageUrl: currentGetPageUrl,
			// Removed mapBounds - not read by consumers (only setMapBounds is used)
			setMapBounds,
			viewMode: basicState.viewMode,
			setViewMode,
		};
		
		// Compare with previous value - if nothing meaningful changed, return the previous value
		const prevValue = prevValueRef.current;
		
		if (prevValue) {
			// Check if any meaningful values changed (ignore callback references)
			// Callbacks are always new references but their behavior is stable
			const queryChanged = prevValue.query !== newValue.query;
			// Use deep equality for filters to avoid false positives from reference changes
			const filtersChanged = (() => {
				const prev = prevValue.filters;
				const curr = newValue.filters;
				if (prev === curr) return false;
				if (typeof prev !== 'object' || typeof curr !== 'object' || prev === null || curr === null) return true;
				const prevKeys = Object.keys(prev);
				const currKeys = Object.keys(curr);
				if (prevKeys.length !== currKeys.length) return true;
				return prevKeys.some(k => prev[k] !== curr[k]);
			})();
			const pageChanged = prevValue.page !== newValue.page;
			// Use deep equality for results to avoid false positives from reference changes
			// Results objects can have new references but same content (from SearchContext updates)
			// IMPORTANT: If resultsVersion changed, we should always consider results changed
			// This ensures that when filters change and new results come in, components re-render
			const resultsChanged = (() => {
				// If resultsVersion changed, always consider results changed (even if content appears same)
				// This is critical for filter changes - new results should trigger re-renders
				if (resultsVersionChanged) {
					return true;
				}
				const prev = prevValue.results;
				const curr = newValue.results;
				if (prev === curr) return false;
				if (!prev || !curr) return prev !== curr;
			// Use areResultsEqual to check if content is actually different
			// Use the memoized areResultsEqual
			return !areResultsEqualMemo(prev, curr);
			})();
			const sortChanged = prevValue.sort !== newValue.sort;
			const viewModeChanged = prevValue.viewMode !== newValue.viewMode;
			const mapResultsChanged = (basicState.viewMode === 'map' && mapResultsMemo !== prevValue.results);
			const isSearchLoadingChanged = prevValue.isSearchLoading !== newValue.isSearchLoading;
			
			const hasMeaningfulChange = 
				queryChanged ||
				filtersChanged ||
				pageChanged ||
				resultsChanged ||
				sortChanged ||
				viewModeChanged ||
				mapResultsChanged ||
				isSearchLoadingChanged ||
				resultsVersionChanged; // Always update if resultsVersion changed
			
			// Check if callbacks changed (for debugging)
			const callbacksChanged = {
				setQuery: prevValue.setQuery !== newValue.setQuery,
				setFilters: prevValue.setFilters !== newValue.setFilters,
				setPage: prevValue.setPage !== newValue.setPage,
				refetch: prevValue.refetch !== newValue.refetch,
				setSort: prevValue.setSort !== newValue.setSort,
				getPageUrl: prevValue.getPageUrl !== newValue.getPageUrl,
				setMapBounds: prevValue.setMapBounds !== newValue.setMapBounds,
				setViewMode: prevValue.setViewMode !== newValue.setViewMode,
			};
			const anyCallbackChanged = Object.values(callbacksChanged).some(Boolean);
			
			// If only callbacks changed, return previous value to prevent re-renders
			if (!hasMeaningfulChange) {
				if (typeof window !== 'undefined' && !isFirstCalc) {
					debugLog('🟢 [SearchContext] No meaningful change - returning PREVIOUS value (preventing re-render)');
				}
				prevValueRef.current = prevValue;
				// Don't update prevResultsVersionRef - version hasn't changed meaningfully
				return prevValue;
			} else {
				if (typeof window !== 'undefined') {
					// Show detailed info about what changed
					const changeDetails: Record<string, any> = {};
					if (queryChanged) {
						changeDetails.query = { prev: prevValue.query, curr: newValue.query };
					}
					if (filtersChanged) {
						changeDetails.filters = {
							prev: Object.keys(prevValue.filters || {}).length,
							curr: Object.keys(newValue.filters || {}).length,
							prevKeys: Object.keys(prevValue.filters || {}),
							currKeys: Object.keys(newValue.filters || {}),
						};
					}
					if (pageChanged) {
						changeDetails.page = { prev: prevValue.page, curr: newValue.page };
					}
					if (resultsChanged) {
						changeDetails.results = {
							prev: { nbHits: prevValue.results?.nbHits, hits: prevValue.results?.hits?.length, page: prevValue.results?.page },
							curr: { nbHits: newValue.results?.nbHits, hits: newValue.results?.hits?.length, page: newValue.results?.page },
							sameRef: prevValue.results === newValue.results,
						};
					}
					if (sortChanged) {
						changeDetails.sort = { prev: prevValue.sort, curr: newValue.sort };
					}
					if (viewModeChanged) {
						changeDetails.viewMode = { prev: prevValue.viewMode, curr: newValue.viewMode };
					}
					if (mapResultsChanged) {
						changeDetails.mapResults = {
							prev: { nbHits: prevValue.results?.nbHits, hits: prevValue.results?.hits?.length },
							curr: { nbHits: mapResults?.nbHits, hits: mapResults?.hits?.length },
						};
					}
					
					debugLog('🔴 [SearchContext] Meaningful change detected:', Object.keys(changeDetails).join(', '));
				}
				// There was a meaningful change - return newValue
				// Update prevResultsVersionRef if version changed (for accurate tracking on next render)
				if (resultsVersionChanged) {
					prevResultsVersionRef.current = resultsVersionMemo;
				}
				prevValueRef.current = newValue;
				return newValue;
			}
		}
		
		// If no previous value, this is the first render - return new value
		prevValueRef.current = newValue;
		if (typeof window !== 'undefined') {
			debugLog('📤 [SearchContext] OUTPUT: returning newValue (first render)', {
				outputQuery: newValue.query,
				outputPage: newValue.page,
				outputSort: newValue.sort,
				outputViewMode: newValue.viewMode,
				outputResultsNbHits: newValue.results?.nbHits,
				outputResultsHitsLength: newValue.results?.hits?.length,
			});
		}
		return newValue;
		// Use the isolated memoized dependencies
		// This ensures we only recalculate when one of these specific dependencies changes
		// Note: stableEffectiveResults is computed inside this useMemo, so we don't include it in deps
		// Callbacks are read from refs, so we don't need them in deps either
	}, [basicState, resultsVersionMemo, filtersObjectMemo, initialResultsMemo, mapResultsMemo, areResultsEqualMemo, results, isLoading, setQuery, setPage, setMapBounds, setViewMode]);

	return (
		<SearchContext.Provider value={value}>{children}</SearchContext.Provider>
	);
}

export function useSearchContext(): SearchContextValue {
	const ctx = useContext(SearchContext);
	if (!ctx) {
		throw new Error('useSearchContext must be used within a SearchProvider');
	}
	return ctx;
}

/** Returns the search context or undefined when not inside SearchProvider (e.g. SRP island before interactive tree mounts). */
export function useSearchContextOptional(): SearchContextValue | undefined {
	return useContext(SearchContext);
}

export function useSearchResults() {
	const { results } = useSearchContext();
	return { results };
}

export function useSearchLoading(): boolean {
	return useSearchContext().isSearchLoading;
}

export function useSearchQuery() {
	const { query, setQuery } = useSearchContext();
	return { query, setQuery };
}

export function useSearchFilters() {
	const { filters, setFilters } = useSearchContext();
	return { filters, setFilters };
}

export function useSearchPage() {
	const { page, setPage } = useSearchContext();
	return { page, setPage };
}

export function useSearchSort() {
  const { sort, setSort } = useSearchContext();
  return { sort, setSort };
}
